import { useState, useCallback, useRef, useEffect } from "react";
import { trpc } from "@/lib/trpc";
import { formatBrazilPhone, formatCpfCnpj, isValidBrazilPhone, isValidCpfCnpj, isValidPastOrTodayDate } from "@/lib/inputFormatters";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { EmptyState } from "@/components/EmptyState";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Search,
  Phone,
  Mail,
  User,
  Calendar,
  TrendingUp,
  Clock,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  MessageCircle,
  Star,
  Download,
  Upload,
  Plus,
  Trash2,
  Edit,
  FileText,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Users,
  Send,
  MessageSquare,
  RefreshCw,
  CheckSquare,
  Square,
  Camera,
} from "lucide-react";
import { useDebounce } from "@/hooks/useDebounce";
import { AppointmentPhotoGallery } from "@/components/AppointmentPhotoGallery";
import { toast } from "sonner";

// ─── Helpers ─────────────────────────────────────────────────────────────────

type AppointmentClient = {
  clientName: string | null;
  clientPhone: string | null;
  clientEmail: string | null;
  lastVisit: number | Date;
  totalVisits: number | string;
  totalSpent: number | string;
};

type DirectClient = {
  id: number;
  salonId: number;
  name: string;
  phone: string | null;
  email: string | null;
  taxDocument?: string | null;
  birthdate: string | null;
  notes: string | null;
  source: string | null;
  photoUrl?: string | null;
  receiveWhatsappNotifications: boolean;
  createdAt: Date | string | number;
};

function formatDate(ts: number | string | Date | null | undefined) {
  if (!ts) return "—";
  const d = ts instanceof Date ? ts : new Date(Number(ts));
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric" });
}

function formatDateTime(ts: number | null | undefined) {
  if (!ts) return "—";
  return new Date(ts).toLocaleString("pt-BR", {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

function formatCurrency(val: number | null | undefined) {
  if (!val) return "R$ 0,00";
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(Number(val));
}

function statusLabel(status: string) {
  const map: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
    confirmed: { label: "Confirmado", variant: "default" },
    completed: { label: "Concluído", variant: "secondary" },
    cancelled: { label: "Cancelado", variant: "destructive" },
    pending: { label: "Pendente", variant: "outline" },
    no_show: { label: "Não compareceu", variant: "destructive" },
  };
  return map[status] ?? { label: status, variant: "outline" };
}

function paymentLabel(method: string | null | undefined) {
  const map: Record<string, string> = {
    cash: "Dinheiro", pix: "PIX", credit_card: "Crédito",
    debit_card: "Débito", voucher: "Voucher", other: "Outro",
  };
  return method ? (map[method] ?? method) : "—";
}

// ─── CSV Helpers ──────────────────────────────────────────────────────────────

function parseCSV(text: string): Array<Record<string, string>> {
  const lines = text.trim().split(/\r?\n/);
  if (lines.length < 2) return [];
  // Suporta separador vírgula (padrão internacional) ou ponto-e-vírgula (Excel PT-BR)
  const sep = lines[0].includes(";") ? ";" : ",";
  const headers = lines[0].split(sep).map((h: any) => h.trim().replace(/^"|"$/g, "").toLowerCase());
  return lines.slice(1).map((line: any) => {
    const values = line.split(sep).map((v: any) => v.trim().replace(/^"|"$/g, ""));
    const obj: Record<string, string> = {};
    headers.forEach((h, i) => { obj[h] = values[i] ?? ""; });
    return obj;
  });
}

async function exportToXLSX(clients: DirectClient[], filename: string) {
  const XLSX = await import("xlsx");
  const rows = clients.map((c: any) => ({
    "Nome": c.name,
    "Telefone": c.phone ?? "",
    "E-mail": c.email ?? "",
    "Data de Nascimento": c.birthdate ?? "",
    "Observações": c.notes ?? "",
    "Origem": c.source ?? "manual",
    "Cadastrado em": formatDate(typeof c.createdAt === "object" ? (c.createdAt as Date).getTime() : Number(c.createdAt)),
  }));
  const ws = XLSX.utils.json_to_sheet(rows, {
    header: ["Nome", "Telefone", "E-mail", "Data de Nascimento", "Observações", "Origem", "Cadastrado em"],
  });
  ws["!cols"] = [{ wch: 30 }, { wch: 18 }, { wch: 30 }, { wch: 20 }, { wch: 30 }, { wch: 12 }, { wch: 18 }];
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Clientes");
  XLSX.writeFile(wb, filename.replace(".csv", ".xlsx"));
}

async function downloadTemplate() {
  const XLSX = await import("xlsx");
  // Aba 1: Modelo com exemplos
  const examples = [
    { "Nome (*obrigatório)": "Maria Silva", "Telefone": "(75) 99999-0001", "E-mail": "maria@email.com", "Data de Nascimento (AAAA-MM-DD)": "1990-05-15", "Observações": "Cliente VIP" },
    { "Nome (*obrigatório)": "João Santos", "Telefone": "(75) 98888-0002", "E-mail": "joao@email.com", "Data de Nascimento (AAAA-MM-DD)": "1985-11-20", "Observações": "Prefere atendimento tarde" },
    { "Nome (*obrigatório)": "Ana Oliveira", "Telefone": "(75) 97777-0003", "E-mail": "", "Data de Nascimento (AAAA-MM-DD)": "", "Observações": "" },
    { "Nome (*obrigatório)": "Carlos Pereira", "Telefone": "(75) 98171-0000", "E-mail": "carlos@email.com", "Data de Nascimento (AAAA-MM-DD)": "2000-03-08", "Observações": "Alergia a amônia" },
  ];
  const headers = ["Nome (*obrigatório)", "Telefone", "E-mail", "Data de Nascimento (AAAA-MM-DD)", "Observações"];
  const ws = XLSX.utils.json_to_sheet(examples, { header: headers });
  ws["!cols"] = [{ wch: 28 }, { wch: 20 }, { wch: 30 }, { wch: 30 }, { wch: 35 }];
  // Aba 2: Instruções
  const instrucoes = [
    { "Campo": "Nome (*obrigatório)", "Descrição": "Nome completo do cliente. OBRIGATÓRIO — linhas sem nome serão ignoradas.", "Exemplo": "Maria Silva" },
    { "Campo": "Telefone", "Descrição": "Telefone completo com DDD. OBRIGATÓRIO; o sistema salva no padrão WhatsApp com código 55.", "Exemplo": "(75) 99999-0001" },
    { "Campo": "E-mail", "Descrição": "Endereço de e-mail do cliente. Opcional.", "Exemplo": "maria@email.com" },
    { "Campo": "Data de Nascimento (AAAA-MM-DD)", "Descrição": "Data no formato AAAA-MM-DD (ano-mês-dia). Opcional.", "Exemplo": "1990-05-15" },
    { "Campo": "Observações", "Descrição": "Notas internas sobre o cliente. Opcional.", "Exemplo": "Cliente VIP, prefere tarde" },
    { "Campo": "", "Descrição": "", "Exemplo": "" },
    { "Campo": "⚠ IMPORTANTE", "Descrição": "Clientes com mesmo telefone ou e-mail serão ATUALIZADOS, não duplicados.", "Exemplo": "" },
    { "Campo": "📄 FORMATO", "Descrição": "Salve como .xlsx ou .csv (UTF-8) antes de importar. Ambos são aceitos.", "Exemplo": "" },
    { "Campo": "🔢 LIMITE", "Descrição": "Máximo de 5.000 clientes por importação.", "Exemplo": "" },
    { "Campo": "📋 SEPARADOR CSV", "Descrição": "Se usar .csv, use vírgula (,) ou ponto-e-vírgula (;) como separador.", "Exemplo": "nome;telefone;e-mail" },
  ];
  const wsInst = XLSX.utils.json_to_sheet(instrucoes, { header: ["Campo", "Descrição", "Exemplo"] });
  wsInst["!cols"] = [{ wch: 32 }, { wch: 65 }, { wch: 28 }];
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Modelo de Importação");
  XLSX.utils.book_append_sheet(wb, wsInst, "Instruções");
  XLSX.writeFile(wb, "modelo_importacao_clientes.xlsx");
}

// ─── Appointment History Modal ────────────────────────────────────────────────

function ClientHistoryModal({ client, salonId, open, onClose }: { client: AppointmentClient | null; salonId?: number; open: boolean; onClose: () => void }) {
  const { data: history, isLoading } = trpc.clients.getHistory.useQuery(
    { clientPhone: client?.clientPhone ?? "" },
    { enabled: open && !!client?.clientPhone }
  );
  const [expandedPhotoApptId, setExpandedPhotoApptId] = useState<number | null>(null);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User className="h-5 w-5 text-primary" />
            Histórico de {client?.clientName ?? "Cliente"}
          </DialogTitle>
        </DialogHeader>
        <div className="flex flex-wrap gap-3 pb-4 border-b">
          {client?.clientPhone && (
            <a href={`https://wa.me/55${client.clientPhone.replace(/\D/g, "")}`} target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-1.5 text-sm text-green-600 hover:text-green-700 font-medium">
              <MessageCircle className="h-4 w-4" />{client.clientPhone}
            </a>
          )}
          {client?.clientEmail && (
            <a href={`mailto:${client.clientEmail}`} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
              <Mail className="h-4 w-4" />{client.clientEmail}
            </a>
          )}
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-3 gap-2 sm:gap-3">
          <div className="bg-muted/40 rounded-lg p-2 sm:p-3 text-center">
            <p className="text-xl sm:text-2xl font-bold text-primary">{client?.totalVisits ?? 0}</p>
            <p className="text-xs text-muted-foreground">Visitas</p>
          </div>
          <div className="bg-muted/40 rounded-lg p-3 text-center">
            <p className="text-xl font-bold text-primary">{formatCurrency(Number(client?.totalSpent ?? 0))}</p>
            <p className="text-xs text-muted-foreground">Total gasto</p>
          </div>
          <div className="bg-muted/40 rounded-lg p-3 text-center">
            <p className="text-sm font-semibold text-primary">{formatDate(Number(client?.lastVisit))}</p>
            <p className="text-xs text-muted-foreground">Última visita</p>
          </div>
        </div>
        {isLoading ? (
          <div className="flex items-center justify-center py-8 text-muted-foreground">
            <Clock className="h-5 w-5 animate-spin mr-2" />Carregando histórico...
          </div>
        ) : !history || history.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Calendar className="h-10 w-10 mx-auto mb-2 opacity-30" />
            <p>Nenhum agendamento encontrado.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Data</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Pagamento</TableHead>
                  <TableHead className="text-right">Valor</TableHead>
                  <TableHead className="text-center w-16">Fotos</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {history.map((appt: any) => {
                  const s = statusLabel(appt.status ?? "");
                  const isExpanded = expandedPhotoApptId === appt.id;
                  return (
                    <>
                      <TableRow key={appt.id}>
                        <TableCell className="text-sm">{formatDateTime(appt.startTime instanceof Date ? appt.startTime.getTime() : Number(appt.startTime))}</TableCell>
                        <TableCell><Badge variant={s.variant}>{s.label}</Badge></TableCell>
                        <TableCell className="text-sm text-muted-foreground">{paymentLabel(appt.paymentMethod)}</TableCell>
                        <TableCell className="text-right font-medium">{formatCurrency(Number(appt.totalPrice ?? 0))}</TableCell>
                        <TableCell className="text-center">
                          <button
                            title="Ver/adicionar fotos do procedimento"
                            onClick={() => setExpandedPhotoApptId(isExpanded ? null : appt.id)}
                            className={`inline-flex items-center justify-center w-8 h-8 rounded-full transition-colors ${
                              isExpanded
                                ? "bg-amber-100 text-amber-700 hover:bg-amber-200"
                                : "text-muted-foreground hover:bg-muted hover:text-foreground"
                            }`}
                          >
                            <Camera className="w-4 h-4" />
                          </button>
                        </TableCell>
                      </TableRow>
                      {isExpanded && salonId && (
                        <TableRow key={`photos-${appt.id}`}>
                          <TableCell colSpan={5} className="bg-muted/30 p-4">
                            <div className="max-w-md">
                              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Fotos do Procedimento</p>
                              <AppointmentPhotoGallery
                                appointmentId={appt.id}
                                salonId={salonId}
                                canEdit={true}
                              />
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ─── Import Modal ─────────────────────────────────────────────────────────────

type ParsedClient = { name: string; phone: string; email?: string | null; birthdate?: string | null; notes?: string | null };

const IMPORT_CHUNK_SIZE = 500;

function ImportModal({ open, onClose, onSuccess }: { open: boolean; onClose: () => void; onSuccess: () => void }) {
  const [parsed, setParsed] = useState<ParsedClient[]>([]);
  const [error, setError] = useState("");
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const utils = trpc.useUtils();

  const importMutation = trpc.clients.import.useMutation({
    onError: (e: any) => toast.error(e.message),
  });

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setError("");
    setParsed([]);
    const file = e.target.files?.[0];
    if (!file) return;
    const isXLSX = file.name.endsWith(".xlsx") || file.name.endsWith(".xls");
    const isCSV = file.name.endsWith(".csv");
    if (!isXLSX && !isCSV) { setError("Apenas arquivos .xlsx, .xls ou .csv são aceitos."); return; }
    const normalizeRow = (row: Record<string, string>): ParsedClient => ({
      name: row["nome (*obrigatório)"] || row["nome"] || row["name"] || "",
      phone: row["telefone"] || row["phone"] || "",
      email: row["e-mail"] || row["email"] || null,
      birthdate: row["data de nascimento (aaaa-mm-dd)"] || row["data de nascimento"] || row["birthdate"] || null,
      notes: row["observações"] || row["observacoes"] || row["notes"] || null,
    });
    if (isXLSX) {
      try {
        const XLSX = await import("xlsx");
        const buffer = await file.arrayBuffer();
        const wb = XLSX.read(buffer, { type: "array" });
        // Usar a primeira aba que não seja "Instruções"
        const sheetName = wb.SheetNames.find((n: string) => !n.toLowerCase().includes("instru")) ?? wb.SheetNames[0];
        const ws = wb.Sheets[sheetName];
        const rawRows: Record<string, string>[] = XLSX.utils.sheet_to_json(ws, { defval: "" });
        const rows = rawRows.map((r: Record<string, string>) => {
          const normalized: Record<string, string> = {};
          for (const k of Object.keys(r)) normalized[k.toLowerCase().trim()] = String(r[k]).trim();
          return normalized;
        });
        if (rows.length === 0) { setError("Planilha vazia ou sem dados válidos."); return; }
        const clients = rows.map(normalizeRow).filter((c) => c.name.trim());
        if (clients.length === 0) { setError("Nenhum cliente com nome válido encontrado. Verifique se a coluna 'Nome' está preenchida."); return; }
        setParsed(clients);
      } catch {
        setError("Erro ao ler o arquivo XLSX. Verifique se o arquivo não está corrompido.");
      }
    } else {
      const reader = new FileReader();
      reader.onload = (ev) => {
        const text = ev.target?.result as string;
        const rows = parseCSV(text).map((r) => {
          const normalized: Record<string, string> = {};
          for (const k of Object.keys(r)) normalized[k.toLowerCase().trim()] = String(r[k]).trim();
          return normalized;
        });
        if (rows.length === 0) { setError("Arquivo CSV vazio ou sem dados válidos."); return; }
        const clients = rows.map(normalizeRow).filter((c) => c.name.trim());
        if (clients.length === 0) { setError("Nenhum cliente com nome válido encontrado. Verifique se a coluna 'Nome' está preenchida."); return; }
        setParsed(clients);
      };
      reader.readAsText(file, "UTF-8");
    }
  };

  const handleImport = async () => {
    if (parsed.length === 0) return;
    const total = parsed.length;
    let done = 0;
    let totalInserted = 0;
    let totalUpdated = 0;
    let totalSkipped = 0;
    setProgress({ done: 0, total });
    try {
      for (let i = 0; i < parsed.length; i += IMPORT_CHUNK_SIZE) {
        const chunk = parsed.slice(i, i + IMPORT_CHUNK_SIZE);
        const result = await importMutation.mutateAsync({ clients: chunk });
        totalInserted += result.inserted;
        totalUpdated += result.updated;
        totalSkipped += result.skipped;
        done = Math.min(i + IMPORT_CHUNK_SIZE, total);
        setProgress({ done, total });
      }
      toast.success(`Importação concluída: ${totalInserted} inseridos, ${totalUpdated} atualizados, ${totalSkipped} ignorados.`);
      utils.clients.invalidate();
      onSuccess();
      onClose();
      setParsed([]);
    } catch (e: any) {
      toast.error(e.message ?? "Erro ao importar clientes.");
    } finally {
      setProgress(null);
    }
  };
  const isImporting = progress !== null;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { onClose(); setParsed([]); setError(""); } }}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5 text-primary" />
            Importar Clientes
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Template download */}
          <div className="flex items-center justify-between p-3 bg-muted/40 rounded-lg">
            <div>
              <p className="text-sm font-medium">Modelo de planilha</p>
              <p className="text-xs text-muted-foreground">Baixe o modelo para preencher corretamente</p>
            </div>
            <Button variant="outline" size="sm" onClick={downloadTemplate}>
              <FileText className="h-4 w-4 mr-2" />
              Baixar modelo (.xlsx)
            </Button>
          </div>

          {/* File input */}
          <div>
            <Label className="text-sm font-medium mb-2 block">Selecionar arquivo (.xlsx ou .csv)</Label>
            <div
              className="border-2 border-dashed border-muted-foreground/30 rounded-lg p-6 text-center cursor-pointer hover:border-primary/50 transition-colors"
              onClick={() => fileRef.current?.click()}
            >
              <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">Clique para selecionar ou arraste o arquivo</p>
              <p className="text-xs text-muted-foreground mt-1">Formatos aceitos: <strong>.xlsx</strong>, <strong>.xls</strong>, <strong>.csv</strong> — máximo 5.000 clientes</p>
              <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={handleFile} />
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="flex items-center gap-2 p-3 bg-destructive/10 text-destructive rounded-lg text-sm">
              <AlertCircle className="h-4 w-4 shrink-0" />
              {error}
            </div>
          )}

          {/* Preview */}
          {parsed.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                <p className="text-sm font-medium text-green-700">{parsed.length} cliente{parsed.length !== 1 ? "s" : ""} encontrado{parsed.length !== 1 ? "s" : ""} — prévia:</p>
              </div>
              <div className="overflow-x-auto border rounded-lg max-h-48">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nome</TableHead>
                      <TableHead>Telefone</TableHead>
                      <TableHead>E-mail</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {parsed.slice(0, 10).map((c, i) => (
                      <TableRow key={i}>
                        <TableCell className="text-sm font-medium">{c.name}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">{c.phone || "—"}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">{c.email || "—"}</TableCell>
                      </TableRow>
                    ))}
                    {parsed.length > 10 && (
                      <TableRow>
                        <TableCell colSpan={3} className="text-center text-xs text-muted-foreground py-2">
                          ... e mais {parsed.length - 10} clientes
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                Clientes com telefone ou e-mail duplicado serão atualizados automaticamente.
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => { onClose(); setParsed([]); setError(""); }}>Cancelar</Button>
          {isImporting && progress && (
            <div className="flex-1 mr-2">
              <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                <span>Importando...</span>
                <span>{progress.done} / {progress.total}</span>
              </div>
              <div className="w-full bg-muted rounded-full h-2">
                <div
                  className="bg-primary h-2 rounded-full transition-all duration-300"
                  style={{ width: `${Math.round((progress.done / progress.total) * 100)}%` }}
                />
              </div>
            </div>
          )}
          <Button onClick={handleImport} disabled={parsed.length === 0 || isImporting}>
            {isImporting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
            {isImporting ? `${Math.round(((progress?.done ?? 0) / (progress?.total ?? 1)) * 100)}%` : `Importar ${parsed.length > 0 ? `${parsed.length} clientes` : ""}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Add/Edit Client Modal ────────────────────────────────────────────────────

function ClientFormModal({ client, open, onClose, onSuccess }: {
  client: DirectClient | null; open: boolean; onClose: () => void; onSuccess: () => void;
}) {
  const [form, setForm] = useState({ name: "", phone: "", email: "", taxDocument: "", birthdate: "", notes: "", receiveWhatsappNotifications: true, sendContractEmail: false });
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [showDetails, setShowDetails] = useState(Boolean(client));
  const [photoBase64, setPhotoBase64] = useState<string | null>(null);
  const [photoMime, setPhotoMime] = useState<string>("image/jpeg");
  const [savedClientId, setSavedClientId] = useState<number | null>(null);
  const photoInputRef = useRef<HTMLInputElement>(null);

  const uploadPhotoMutation = (trpc as any).clients.uploadPhoto.useMutation({
    onSuccess: (data: any) => { toast.success("Foto atualizada!"); onSuccess(); },
    onError: (e: any) => toast.error("Erro ao enviar foto: " + e.message),
  });

  const upsertMutation = trpc.clients.upsert.useMutation({
    onSuccess: () => { toast.success("Cliente adicionado!"); onSuccess(); onClose(); },
    onError: (e: any) => toast.error(e.message),
  });

  const updateMutation = (trpc as any).clients.update.useMutation({
    onSuccess: () => { toast.success("Cliente atualizado!"); onSuccess(); onClose(); },
    onError: (e: any) => toast.error(e.message),
  });

  useEffect(() => {
    if (!open) return;
    if (client) {
      setForm({ name: client.name, phone: formatBrazilPhone(client.phone ?? ""), email: client.email ?? "", taxDocument: formatCpfCnpj(client.taxDocument ?? ""), birthdate: client.birthdate ?? "", notes: client.notes ?? "", receiveWhatsappNotifications: client.receiveWhatsappNotifications ?? true, sendContractEmail: false });
      setPhotoPreview(client.photoUrl ?? null);
      setPhotoBase64(null);
      setSavedClientId(client.id);
    } else {
      setForm({ name: "", phone: "", email: "", taxDocument: "", birthdate: "", notes: "", receiveWhatsappNotifications: true, sendContractEmail: false });
      setPhotoPreview(null);
      setPhotoBase64(null);
      setSavedClientId(null);
    }
  }, [open, client]);

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Foto muito grande. Máximo 5MB."); return; }
    setPhotoMime(file.type);
    const reader = new FileReader();
    reader.onload = (ev) => {
      const result = ev.target?.result as string;
      setPhotoPreview(result);
      setPhotoBase64(result);
    };
    reader.readAsDataURL(file);
  };

  const handleSave = () => {
    const phone = formatBrazilPhone(form.phone);
    const email = form.email.trim();
    if (!form.name.trim()) {
      toast.error("Informe o nome do cliente");
      return;
    }
    if (!isValidBrazilPhone(phone)) {
      toast.error("Informe um telefone válido com DDD.");
      return;
    }
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Informe um e-mail válido.");
      return;
    }
    if (form.taxDocument && !isValidCpfCnpj(form.taxDocument)) {
      toast.error("Informe um CPF ou CNPJ válido.");
      return;
    }
    if (!isValidPastOrTodayDate(form.birthdate)) {
      toast.error("A data de nascimento não pode estar no futuro.");
      return;
    }
    if (client?.id) {
      updateMutation.mutate(
        { id: client.id, name: form.name.trim(), phone, email: email || null, taxDocument: form.taxDocument || null, birthdate: form.birthdate || null, notes: form.notes || null, receiveWhatsappNotifications: form.receiveWhatsappNotifications },
        { onSuccess: () => {
          if (photoBase64) uploadPhotoMutation.mutate({ clientId: client.id, base64: photoBase64, mimeType: photoMime });
          else { toast.success("Cliente atualizado!"); onSuccess(); onClose(); }
        }}
      );
    } else {
      upsertMutation.mutate(
        { name: form.name.trim(), phone, email: email || null, taxDocument: form.taxDocument || null, birthdate: form.birthdate || null, notes: form.notes || null, sendContractEmail: form.sendContractEmail },
        { onSuccess: (data: any) => {
          if (photoBase64 && data?.id) uploadPhotoMutation.mutate({ clientId: data.id, base64: photoBase64, mimeType: photoMime });
          else { toast.success("Cliente adicionado!"); onSuccess(); onClose(); }
        }}
      );
    }
  };

  const isPending = upsertMutation.isPending || updateMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="flex w-[calc(100vw-1rem)] max-w-md max-h-[calc(100dvh-1rem)] flex-col gap-0 overflow-hidden p-0 sm:w-[calc(100vw-2rem)]">
        <DialogHeader className="shrink-0 border-b bg-card px-4 py-4 pr-12">
          <DialogTitle className="flex items-center gap-2">
            {client ? <><Edit className="h-4 w-4 text-primary" />Editar Cliente</> : <><Plus className="h-4 w-4 text-primary" />Adicionar Cliente</>}
          </DialogTitle>
        </DialogHeader>
        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto overscroll-contain px-4 py-4 pb-5">
          <div className="rounded-xl border border-primary/15 bg-primary/5 p-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-primary">Dados essenciais</p>
            <p className="mt-1 text-xs text-muted-foreground">Nome e telefone são obrigatórios para cadastrar e enviar confirmações pelo WhatsApp.</p>
          </div>
          <div>
            <Label className="text-sm">Nome *</Label>
            <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Nome completo" className="mt-1" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-sm">Telefone *</Label>
              <Input value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: formatBrazilPhone(e.target.value) }))} inputMode="tel" placeholder="(75) 98171-0000" className="mt-1" />
              <p className="mt-1 text-xs text-muted-foreground">Obrigatório: DDD + número completo. Será salvo no padrão WhatsApp com 55.</p>
            </div>
          </div>

          <button type="button" onClick={() => setShowDetails((visible) => !visible)} className="flex w-full items-center justify-between rounded-xl border bg-muted/30 px-3 py-2.5 text-left transition-colors hover:bg-muted/60">
            <span><span className="block text-sm font-medium">Informações adicionais</span><span className="block text-xs text-muted-foreground">Foto, nascimento, e-mail, observações e preferências</span></span>
            <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${showDetails ? "rotate-180" : ""}`} />
          </button>

          {showDetails && <div className="space-y-3 rounded-xl border bg-card p-3">
            {/* Foto do cliente */}
            <div className="flex items-center gap-3">
              <div className="relative w-14 h-14 shrink-0 rounded-full overflow-hidden border-2 border-dashed border-muted-foreground/30 bg-muted/30 flex items-center justify-center cursor-pointer hover:border-primary/60 transition-colors" onClick={() => photoInputRef.current?.click()}>
                {photoPreview ? <img src={photoPreview} alt="Foto" className="w-full h-full object-cover" /> : <Camera className="h-5 w-5 text-muted-foreground/50" />}
              </div>
              <div><button type="button" className="text-sm text-primary hover:underline" onClick={() => photoInputRef.current?.click()}>Adicionar foto</button><p className="text-xs text-muted-foreground">Opcional para identificar a cliente.</p></div>
              <input ref={photoInputRef} type="file" accept="image/*" className="hidden" onChange={handlePhotoChange} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label className="text-sm">Data de Nascimento</Label><Input value={form.birthdate} onChange={(e) => setForm((f) => ({ ...f, birthdate: e.target.value }))} type="date" max={new Date().toISOString().slice(0, 10)} className="mt-1" /></div>
              <div><Label className="text-sm">E-mail</Label><Input value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} placeholder="email@exemplo.com" type="email" className="mt-1" /></div>
            </div>
            <div><Label className="text-sm">CPF ou CNPJ</Label><Input value={form.taxDocument} onChange={(e) => setForm((f) => ({ ...f, taxDocument: formatCpfCnpj(e.target.value) }))} inputMode="numeric" placeholder="Opcional para nota fiscal" className="mt-1" /><p className="mt-1 text-xs text-muted-foreground">Usado para pré-preencher dados fiscais quando necessário.</p></div>
            <div><Label className="text-sm">Observações</Label><Textarea value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} placeholder="Preferências, alergias, histórico médico relevante..." rows={3} className="mt-1 resize-none" /></div>
          {/* Toggle envio de contrato por e-mail (apenas para novos clientes com e-mail) */}
          {!client && (
            <div className="flex items-center justify-between p-3 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg">
              <div>
                <p className="text-sm font-medium flex items-center gap-1.5"><Mail className="w-3.5 h-3.5 text-blue-600" />Enviar contrato por e-mail</p>
                <p className="text-xs text-muted-foreground">Envia o PDF do contrato de serviços ao e-mail informado</p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={form.sendContractEmail}
                disabled={!form.email}
                onClick={() => setForm((f) => ({ ...f, sendContractEmail: !f.sendContractEmail }))}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-40 ${
                  form.sendContractEmail ? "bg-blue-500" : "bg-muted-foreground/40"
                }`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                  form.sendContractEmail ? "translate-x-6" : "translate-x-1"
                }`} />
              </button>
            </div>
          )}
          {/* Toggle de opt-out de WhatsApp (LGPD) */}
          <div className="flex items-center justify-between p-3 bg-muted/40 rounded-lg">
            <div>
              <p className="text-sm font-medium">Notificações via WhatsApp</p>
              <p className="text-xs text-muted-foreground">Confirmações e lembretes de agendamento</p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={form.receiveWhatsappNotifications}
              onClick={() => setForm((f) => ({ ...f, receiveWhatsappNotifications: !f.receiveWhatsappNotifications }))}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${
                form.receiveWhatsappNotifications ? "bg-green-500" : "bg-muted-foreground/40"
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                  form.receiveWhatsappNotifications ? "translate-x-6" : "translate-x-1"
                }`}
              />
            </button>
          </div>
          </div>}
        </div>
        <DialogFooter className="shrink-0 border-t bg-card px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={handleSave} disabled={!form.name.trim() || !form.phone.trim() || isPending} className="bg-primary text-primary-foreground">
            {isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            {client ? "Salvar alterações" : "Adicionar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Agent Campaign Tab Component ─────────────────────────────────────────────────────────────────────────────────────

function AgentCampaignTab() {
  const [search, setSearch] = useState("");
  const [selectedPhones, setSelectedPhones] = useState<string[]>([]);
  const [message, setMessage] = useState("");
  const [resultOpen, setResultOpen] = useState(false);
  const [campaignResult, setCampaignResult] = useState<{ sent: number; failed: number; total: number; errors: string[] } | null>(null);

  const { data: contactsData, isLoading } = (trpc as any).clients.listAgentContacts.useQuery(
    { search, channel: "whatsapp" },
    { enabled: true }
  );

  const contacts: Array<{
    id: number;
    clientName: string | null;
    clientPhone: string;
    channel: string;
    status: string;
    lastMessageAt: string | null;
    messageCount: number;
    receiveWhatsappNotifications: boolean | null;
  }> = contactsData ?? [];

  const campaignMutation = (trpc as any).clients.sendAgentCampaign.useMutation({
    onSuccess: (data: any) => {
      setCampaignResult(data);
      setResultOpen(true);
      setSelectedPhones([]);
      toast.success(`Campanha enviada: ${data.sent} mensagens enviadas com sucesso!`);
    },
    onError: (e: any) => toast.error(e.message),
  });

  const toggleSelect = (phone: string) => {
    setSelectedPhones((prev) => prev.includes(phone) ? prev.filter((x) => x !== phone) : [...prev, phone]);
  };

  const toggleAll = () => {
    const eligible = contacts.filter((c) => c.receiveWhatsappNotifications !== false);
    if (selectedPhones.length === eligible.length) setSelectedPhones([]);
    else setSelectedPhones(eligible.map((c) => c.clientPhone));
  };

  const handleSend = () => {
    if (selectedPhones.length === 0) { toast.error("Selecione ao menos um contato."); return; }
    if (!message.trim()) { toast.error("Digite a mensagem da campanha."); return; }
    campaignMutation.mutate({ phones: selectedPhones, message: message.trim() });
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric" });
  };

  const eligibleContacts = contacts.filter((c) => c.receiveWhatsappNotifications !== false);

  return (
    <div className="space-y-4">
      {/* Header */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <MessageCircle className="h-5 w-5 text-green-500" />
                Campanha via Agente WhatsApp
              </CardTitle>
              <p className="text-sm text-muted-foreground mt-1">
                Dispare mensagens em massa para contatos que conversaram com o agente nativo via WhatsApp.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Buscar por nome ou telefone..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-8 w-56"
                />
              </div>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Mensagem da campanha */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm">Mensagem da Campanha</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <Textarea
              placeholder="Digite sua mensagem de campanha aqui... Ex: Olá! Temos uma promoção especial para você..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={5}
              maxLength={338}
            />
            <div className="flex items-center justify-between mt-1">
              <p className="text-xs text-muted-foreground">{message.length}/338 caracteres</p>
              {message.length > 338 && (
                <p className="text-xs text-destructive">Mensagem muito longa para WhatsApp</p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Lista de contatos */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm">
              {isLoading ? "Carregando..." : `${contacts.length} contato${contacts.length !== 1 ? "s" : ""} do agente`}
            </CardTitle>
            {contacts.length > 0 && (
              <Button variant="ghost" size="sm" onClick={toggleAll}>
                {selectedPhones.length === eligibleContacts.length && eligibleContacts.length > 0 ? (
                  <><CheckSquare className="h-4 w-4 mr-1" />Desmarcar todos</>
                ) : (
                  <><Square className="h-4 w-4 mr-1" />Selecionar todos</>
                )}
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-12 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin mr-2" />Buscando contatos do agente...
            </div>
          ) : contacts.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <MessageCircle className="h-12 w-12 mx-auto mb-3 text-muted-foreground opacity-40" />
              <p className="font-medium">Nenhum contato encontrado</p>
              <p className="text-sm mt-1">Os contatos que conversarem com o agente nativo via WhatsApp aparecerão aqui.</p>
            </div>
          ) : (
            <div className="divide-y max-h-[500px] overflow-y-auto">
              {contacts.map((contact) => {
                const isSelected = selectedPhones.includes(contact.clientPhone);
                const optedOut = contact.receiveWhatsappNotifications === false;
                return (
                  <div
                    key={contact.id}
                    className={`flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-muted/30 transition-colors ${
                      isSelected ? "bg-green-50 dark:bg-green-950/20" : ""
                    } ${optedOut ? "opacity-50 cursor-not-allowed" : ""}`}
                    onClick={() => !optedOut && toggleSelect(contact.clientPhone)}
                  >
                    <div className="flex-shrink-0">
                      {isSelected ? (
                        <CheckSquare className="h-5 w-5 text-green-500" />
                      ) : (
                        <Square className="h-5 w-5 text-muted-foreground" />
                      )}
                    </div>
                    <div className="h-9 w-9 rounded-full bg-green-500/10 flex items-center justify-center flex-shrink-0">
                      <span className="text-sm font-semibold text-green-600">
                        {(contact.clientName || "?").charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">
                        {contact.clientName || "Sem nome"}
                      </p>
                      <div className="flex items-center gap-3 mt-0.5">
                        <span className="text-xs text-muted-foreground flex items-center gap-1">
                          <Phone className="h-3 w-3" />{contact.clientPhone}
                        </span>
                        <span className="text-xs text-muted-foreground flex items-center gap-1">
                          <MessageCircle className="h-3 w-3" />
                          {contact.messageCount} msg
                        </span>
                      </div>
                    </div>
                    <div className="text-right shrink-0 flex flex-col items-end gap-1">
                      {optedOut && (
                        <Badge variant="secondary" className="text-xs">Opt-out</Badge>
                      )}
                      <span className="text-xs text-muted-foreground">
                        Última: {formatDate(contact.lastMessageAt)}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Botão de envio */}
      {contacts.length > 0 && (
        <div className="flex items-center justify-between bg-muted/50 rounded-lg p-4">
          <p className="text-sm text-muted-foreground">
            {selectedPhones.length > 0
              ? `${selectedPhones.length} contato${selectedPhones.length !== 1 ? "s" : ""} selecionado${selectedPhones.length !== 1 ? "s" : ""}`
              : "Selecione os contatos para enviar a campanha"}
          </p>
          <Button
            onClick={handleSend}
            disabled={selectedPhones.length === 0 || campaignMutation.isPending || !message.trim()}
            className="bg-green-500 hover:bg-green-600 text-white"
          >
            {campaignMutation.isPending ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Enviando...</>
            ) : (
              <><Send className="h-4 w-4 mr-2" />Enviar Campanha ({selectedPhones.length})</>
            )}
          </Button>
        </div>
      )}

      {/* Modal de resultado */}
      <Dialog open={resultOpen} onOpenChange={setResultOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Resultado da Campanha</DialogTitle>
          </DialogHeader>
          {campaignResult && (
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-2 sm:gap-3">
                <div className="text-center p-2 sm:p-3 bg-green-50 dark:bg-green-950/20 rounded-lg">
                  <p className="text-xl sm:text-2xl font-bold text-green-600">{campaignResult.sent}</p>
                  <p className="text-xs text-muted-foreground">Enviados</p>
                </div>
                <div className="text-center p-3 bg-red-50 dark:bg-red-950/20 rounded-lg">
                  <p className="text-2xl font-bold text-red-600">{campaignResult.failed}</p>
                  <p className="text-xs text-muted-foreground">Falhas</p>
                </div>
                <div className="text-center p-3 bg-muted rounded-lg">
                  <p className="text-2xl font-bold">{campaignResult.total}</p>
                  <p className="text-xs text-muted-foreground">Total</p>
                </div>
              </div>
              {campaignResult.errors.length > 0 && (
                <div className="mt-3">
                  <p className="text-sm font-medium text-destructive mb-2">Erros:</p>
                  <div className="max-h-32 overflow-y-auto space-y-1">
                    {campaignResult.errors.map((err, i) => (
                      <p key={i} className="text-xs text-muted-foreground">{err}</p>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setResultOpen(false)}>Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Reactivation Tab Component ─────────────────────────────────────────────────────────────────────────────────────

function ReactivationTab() {
  const [daysInactive, setDaysInactive] = useState(60);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [offerText, setOfferText] = useState("");
  const [customMessage, setCustomMessage] = useState("");
  const [useCustom, setUseCustom] = useState(false);
  const [resultOpen, setResultOpen] = useState(false);
  const [campaignResult, setCampaignResult] = useState<{ sent: number; failed: number; total: number; errors: string[] } | null>(null);

  const { data: inactiveData, isLoading, refetch } = (trpc as any).clients.getInactive.useQuery(
    { daysInactive },
    { enabled: true }
  );
  const inactiveClients: Array<{ id: number; name: string; phone: string; lastVisit: number | null; daysSinceLastVisit: number }> = inactiveData ?? [];

  const campaignMutation = (trpc as any).clients.sendReactivationCampaign.useMutation({
    onSuccess: (data: any) => {
      setCampaignResult(data);
      setResultOpen(true);
      setSelectedIds([]);
      toast.success(`Campanha enviada: ${data.sent} mensagens enviadas com sucesso!`);
    },
    onError: (e: any) => toast.error(e.message),
  });

  const toggleSelect = (id: number) => {
    setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const toggleAll = () => {
    if (selectedIds.length === inactiveClients.length) setSelectedIds([]);
    else setSelectedIds(inactiveClients.map((c) => c.id));
  };

  const handleSend = () => {
    if (selectedIds.length === 0) { toast.error("Selecione ao menos um cliente."); return; }
    campaignMutation.mutate({
      clientIds: selectedIds,
      offerText: offerText.trim() || undefined,
      customMessage: useCustom && customMessage.trim() ? customMessage.trim() : undefined,
    });
  };

  const formatLastVisit = (ms: number | null) => {
    if (!ms) return "Nunca agendou";
    return new Date(ms).toLocaleDateString("pt-BR");
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <MessageSquare className="h-5 w-5 text-amber-500" />
                Campanha de Reativação
              </CardTitle>
              <p className="text-sm text-muted-foreground mt-1">
                Envie mensagens personalizadas via WhatsApp para clientes que não agendaram recentemente.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-sm whitespace-nowrap">Inativos há mais de:</Label>
              <select
                className="border rounded px-2 py-1 text-sm bg-background"
                value={daysInactive}
                onChange={(e) => { setDaysInactive(Number(e.target.value)); setSelectedIds([]); }}
              >
                <option value={30}>30 dias</option>
                <option value={60}>60 dias</option>
                <option value={90}>90 dias</option>
                <option value={180}>6 meses</option>
                <option value={365}>1 ano</option>
              </select>
              <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isLoading}>
                <RefreshCw className={`h-4 w-4 ${isLoading ? "animate-spin" : ""}`} />
              </Button>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Mensagem da campanha */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm">Mensagem da Campanha</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <Label className="text-sm">Oferta especial (opcional)</Label>
            <Input
              placeholder="Ex: 20% de desconto no próximo agendamento!"
              value={offerText}
              onChange={(e) => setOfferText(e.target.value)}
              className="mt-1"
              maxLength={200}
            />
            <p className="text-xs text-muted-foreground mt-1">{offerText.length}/200 caracteres</p>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="useCustom"
              checked={useCustom}
              onChange={(e) => setUseCustom(e.target.checked)}
              className="rounded"
            />
            <Label htmlFor="useCustom" className="text-sm cursor-pointer">Usar mensagem personalizada (substitui o texto padrão)</Label>
          </div>
          {useCustom && (
            <div>
              <Textarea
                placeholder="Digite sua mensagem personalizada aqui..."
                value={customMessage}
                onChange={(e) => setCustomMessage(e.target.value)}
                rows={4}
                maxLength={1000}
              />
              <p className="text-xs text-muted-foreground mt-1">{customMessage.length}/1000 caracteres</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Lista de clientes inativos */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm">
              {isLoading ? "Carregando..." : `${inactiveClients.length} cliente${inactiveClients.length !== 1 ? "s" : ""} inativo${inactiveClients.length !== 1 ? "s" : ""}`}
            </CardTitle>
            {inactiveClients.length > 0 && (
              <Button variant="ghost" size="sm" onClick={toggleAll}>
                {selectedIds.length === inactiveClients.length ? (
                  <><CheckSquare className="h-4 w-4 mr-1" />Desmarcar todos</>
                ) : (
                  <><Square className="h-4 w-4 mr-1" />Selecionar todos</>
                )}
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-12 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin mr-2" />Buscando clientes inativos...
            </div>
          ) : inactiveClients.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <CheckCircle2 className="h-12 w-12 mx-auto mb-3 text-green-500 opacity-60" />
              <p className="font-medium">Nenhum cliente inativo encontrado!</p>
              <p className="text-sm mt-1">Todos os seus clientes agendaram nos últimos {daysInactive} dias. 🎉</p>
            </div>
          ) : (
            <div className="divide-y">
              {inactiveClients.map((client) => (
                <div
                  key={client.id}
                  className={`flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-muted/30 transition-colors ${
                    selectedIds.includes(client.id) ? "bg-amber-50 dark:bg-amber-950/20" : ""
                  }`}
                  onClick={() => toggleSelect(client.id)}
                >
                  <div className="flex-shrink-0">
                    {selectedIds.includes(client.id) ? (
                      <CheckSquare className="h-5 w-5 text-amber-500" />
                    ) : (
                      <Square className="h-5 w-5 text-muted-foreground" />
                    )}
                  </div>
                  <div className="h-9 w-9 rounded-full bg-amber-500/10 flex items-center justify-center flex-shrink-0">
                    <span className="text-sm font-semibold text-amber-600">{client.name.charAt(0).toUpperCase()}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{client.name}</p>
                    <div className="flex items-center gap-3 mt-0.5">
                      <span className="text-xs text-muted-foreground flex items-center gap-1">
                        <Phone className="h-3 w-3" />{client.phone}
                      </span>
                      <span className="text-xs text-muted-foreground flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {client.daysSinceLastVisit >= 999 ? "Nunca agendou" : `Há ${client.daysSinceLastVisit} dias`}
                      </span>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-xs text-muted-foreground">Última visita</p>
                    <p className="text-xs font-medium">{formatLastVisit(client.lastVisit)}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Botão de envio */}
      {inactiveClients.length > 0 && (
        <div className="flex items-center justify-between bg-muted/50 rounded-lg p-4">
          <p className="text-sm text-muted-foreground">
            {selectedIds.length > 0
              ? `${selectedIds.length} cliente${selectedIds.length !== 1 ? "s" : ""} selecionado${selectedIds.length !== 1 ? "s" : ""}`
              : "Selecione os clientes para enviar a campanha"}
          </p>
          <Button
            onClick={handleSend}
            disabled={selectedIds.length === 0 || campaignMutation.isPending}
            className="bg-amber-500 hover:bg-amber-600 text-white"
          >
            {campaignMutation.isPending ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Enviando...</>
            ) : (
              <><Send className="h-4 w-4 mr-2" />Enviar Campanha ({selectedIds.length})</>
            )}
          </Button>
        </div>
      )}

      {/* Modal de resultado */}
      <Dialog open={resultOpen} onOpenChange={setResultOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Resultado da Campanha</DialogTitle>
          </DialogHeader>
          {campaignResult && (
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-2 sm:gap-3">
                <div className="text-center p-2 sm:p-3 bg-green-50 dark:bg-green-950/20 rounded-lg">
                  <p className="text-xl sm:text-2xl font-bold text-green-600">{campaignResult.sent}</p>
                  <p className="text-xs text-muted-foreground">Enviados</p>
                </div>
                <div className="text-center p-3 bg-red-50 dark:bg-red-950/20 rounded-lg">
                  <p className="text-2xl font-bold text-red-600">{campaignResult.failed}</p>
                  <p className="text-xs text-muted-foreground">Falhas</p>
                </div>
                <div className="text-center p-3 bg-muted rounded-lg">
                  <p className="text-2xl font-bold">{campaignResult.total}</p>
                  <p className="text-xs text-muted-foreground">Total</p>
                </div>
              </div>
              {campaignResult.errors.length > 0 && (
                <div className="mt-3">
                  <p className="text-sm font-medium text-destructive mb-2">Erros:</p>
                  <div className="max-h-32 overflow-y-auto space-y-1">
                    {campaignResult.errors.map((err, i) => (
                      <p key={i} className="text-xs text-muted-foreground">{err}</p>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setResultOpen(false)}>Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/// ─── Main Component ─────────────────────────────────────────────────────────────────────────────────────
export default function AdminClients() {
  const [tab, setTab] = useState<"appointments" | "direct" | "reactivation" | "agentCampaign">("appointments");

  // Appointment clients state
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [selectedClient, setSelectedClient] = useState<AppointmentClient | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const debouncedSearch = useDebounce(search, 400);

  // Direct clients state
  const [directSearch, setDirectSearch] = useState("");
  const [directPage, setDirectPage] = useState(1);
  const [directSegment, setDirectSegment] = useState<"all" | "birthdays">("all");
  const [importOpen, setImportOpen] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editingClient, setEditingClient] = useState<DirectClient | null>(null);
  const [deleteClientId, setDeleteClientId] = useState<number | null>(null);
  const debouncedDirectSearch = useDebounce(directSearch, 400);

  const utils = trpc.useUtils();
  const { data: mySalon } = trpc.salon.getMySalon.useQuery();

  // Queries
  const { data, isLoading } = trpc.clients.list.useQuery({ search: debouncedSearch, page, pageSize: 20 });
  const { data: directData, isLoading: directLoading } = (trpc as any).clients.listDirect.useQuery(
    { search: debouncedDirectSearch, page: directPage, pageSize: 50 },
    { enabled: tab === "direct" }
  );
  const { data: birthdays = [], isLoading: birthdaysLoading } = (trpc as any).clients.getBirthdays.useQuery(
    undefined,
    { enabled: tab === "direct" }
  );
  const exportQuery = trpc.clients.exportAll.useQuery(undefined, { enabled: false });

  const deleteMutation = trpc.clients.delete.useMutation({
    onSuccess: () => { toast.success("Cliente removido."); (utils as any).clients.listDirect.invalidate(); },
    onError: (e: any) => toast.error(e.message),
  });

  const clients = data?.clients ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / 20);

  const directClients = (directData?.clients ?? []) as unknown as DirectClient[];
  const birthdayClients = birthdays as DirectClient[];
  const visibleDirectClients = directSegment === "birthdays" ? birthdayClients : directClients;
  const directTotal = directData?.total ?? 0;
  const directTotalPages = Math.ceil(directTotal / 50);

  const handleExport = async () => {
    const result = await exportQuery.refetch();
    if (result.data && result.data.length > 0) {
      await exportToXLSX(result.data as unknown as DirectClient[], `clientes_${new Date().toISOString().slice(0, 10)}.xlsx`);
      toast.success(`${result.data.length} clientes exportados com sucesso!`);
    } else {
      toast.info("Nenhum cliente na base para exportar.");
    }
  };

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header */}
      <div className="rounded-2xl border bg-card p-4 shadow-sm md:p-5">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-xl md:text-2xl font-bold">Clientes</h1>
            <Badge variant="secondary" className="font-normal">{directTotal || total} cadastrados</Badge>
          </div>
          <p className="text-muted-foreground text-sm mt-1">Cadastre, localize e acompanhe o relacionamento com sua base.</p>
        </div>
        <div className="grid grid-cols-2 sm:flex gap-2 w-full sm:w-auto">
          <Button variant="outline" size="sm" onClick={() => setImportOpen(true)} className="w-full sm:w-auto">
            <Upload className="h-4 w-4 mr-2" />
            Importar CSV
          </Button>
          <Button variant="outline" size="sm" onClick={handleExport} disabled={exportQuery.isFetching} className="w-full sm:w-auto">
            {exportQuery.isFetching ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
            Exportar Excel
          </Button>
          <Button size="sm" onClick={() => { setEditingClient(null); setFormOpen(true); }} className="col-span-2 sm:col-span-1 w-full sm:w-auto">
            <Plus className="h-4 w-4 mr-2" />
            Novo Cliente
          </Button>
        </div>
      </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg"><User className="h-5 w-5 text-primary" /></div>
              <div>
                <p className="text-2xl font-bold">{total}</p>
                <p className="text-xs text-muted-foreground">Via agendamentos</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500/10 rounded-lg"><Users className="h-5 w-5 text-blue-600" /></div>
              <div>
                <p className="text-2xl font-bold">{directTotal}</p>
                <p className="text-xs text-muted-foreground">Base de clientes</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-500/10 rounded-lg"><Star className="h-5 w-5 text-amber-600" /></div>
              <div>
                <p className="text-2xl font-bold">
                  {clients.filter((c: any) => Number(c.totalVisits) >= 5).length}
                </p>
                <p className="text-xs text-muted-foreground">Clientes fiéis (5+ visitas)</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={tab} onValueChange={(v) => setTab(v as "appointments" | "direct" | "reactivation")}>
        {/* Mobile: caixa suspensa */}
        <div className="block md:hidden mb-2">
          <Select value={tab} onValueChange={(v) => setTab(v as "appointments" | "direct" | "reactivation" | "agentCampaign")}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Selecione a seção" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="appointments">Via Agendamentos</SelectItem>
              <SelectItem value="direct">Base de Clientes</SelectItem>
              <SelectItem value="reactivation">Reativação</SelectItem>
              <SelectItem value="agentCampaign">Agente WhatsApp</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {/* Desktop: abas normais */}
        <TabsList className="hidden md:inline-flex">
          <TabsTrigger value="appointments">
            <Calendar className="h-4 w-4 mr-2" />
            Via Agendamentos
          </TabsTrigger>
          <TabsTrigger value="direct">
            <Users className="h-4 w-4 mr-2" />
            Base de Clientes
          </TabsTrigger>
          <TabsTrigger value="reactivation">
            <MessageSquare className="h-4 w-4 mr-2" />
            Reativação
          </TabsTrigger>
          <TabsTrigger value="agentCampaign">
            <MessageCircle className="h-4 w-4 mr-2" />
            Agente WhatsApp
          </TabsTrigger>
        </TabsList>

        {/* ── Aba: Via Agendamentos ── */}
        <TabsContent value="appointments" className="space-y-4 mt-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Buscar clientes</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar por nome, telefone ou e-mail..."
                  value={search}
                  onChange={useCallback((e: React.ChangeEvent<HTMLInputElement>) => { setSearch(e.target.value); setPage(1); }, [])}
                  className="pl-9"
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-0">
              {isLoading ? (
                <div className="flex items-center justify-center py-16 text-muted-foreground">
                  <Clock className="h-5 w-5 animate-spin mr-2" />Carregando clientes...
                </div>
              ) : clients.length === 0 ? (
                <EmptyState
                  icon={User}
                  title={search ? "Nenhum cliente encontrado" : "Sua base de clientes está vazia"}
                  description={search ? "Tente buscar pelo nome, telefone ou e-mail do cliente." : "Cadastre o primeiro cliente agora ou ele será incluído automaticamente no primeiro agendamento."}
                  actionLabel={search ? "Limpar busca" : "Cadastrar cliente"}
                  onAction={() => search ? setSearch("") : (setEditingClient(null), setFormOpen(true))}
                />
              ) : (
                <div className="divide-y">
                  {clients.map((client, idx) => (
                    <div
                      key={`${client.clientPhone}-${idx}`}
                      className="flex items-center justify-between px-3 md:px-6 py-3 md:py-4 hover:bg-muted/30 transition-colors cursor-pointer gap-2"
                      onClick={() => { setSelectedClient(client as AppointmentClient); setHistoryOpen(true); }}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <span className="text-sm font-semibold text-primary">{(client.clientName ?? "?").charAt(0).toUpperCase()}</span>
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-sm truncate">{client.clientName ?? "—"}</p>
                          <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-0.5">
                            {client.clientPhone && (
                              <span className="text-xs text-muted-foreground flex items-center gap-1">
                                <Phone className="h-3 w-3 shrink-0" /><span className="truncate">{client.clientPhone}</span>
                              </span>
                            )}
                            {client.clientEmail && (
                              <span className="text-xs text-muted-foreground hidden sm:flex items-center gap-1">
                                <Mail className="h-3 w-3 shrink-0" /><span className="truncate max-w-[120px]">{String(client.clientEmail)}</span>
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 text-right shrink-0">
                        <div className="hidden sm:block">
                          <p className="text-sm font-medium whitespace-nowrap">{Number(client.totalVisits)} visita{Number(client.totalVisits) !== 1 ? "s" : ""}</p>
                          <p className="text-xs text-muted-foreground whitespace-nowrap">Última: {formatDate(Number(client.lastVisit))}</p>
                        </div>
                        <div className="hidden md:block">
                          <p className="text-sm font-semibold text-green-600 whitespace-nowrap">{formatCurrency(Number(client.totalSpent))}</p>
                          <p className="text-xs text-muted-foreground">Total gasto</p>
                        </div>
                        <Button variant="ghost" size="sm" className="text-xs shrink-0">
                          <span className="hidden sm:inline">Ver histórico</span>
                          <span className="sm:hidden">Ver</span>
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {totalPages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">Página {page} de {totalPages}</p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>
                  <ChevronLeft className="h-4 w-4" />Anterior
                </Button>
                <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}>
                  Próxima<ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </TabsContent>

        {/* ── Aba: Base de Clientes ── */}
        <TabsContent value="direct" className="space-y-4 mt-4">
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant={directSegment === "all" ? "default" : "outline"} onClick={() => setDirectSegment("all")}>
              Todos ({directTotal})
            </Button>
            <Button size="sm" variant={directSegment === "birthdays" ? "default" : "outline"} className={directSegment !== "birthdays" ? "border-pink-200 text-pink-700" : ""} onClick={() => setDirectSegment("birthdays")}>
              <Star className="h-3.5 w-3.5 mr-1.5" />Aniversariantes do mês ({birthdayClients.length})
            </Button>
          </div>
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Buscar na base</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar por nome, telefone ou e-mail..."
                  value={directSearch}
                  onChange={useCallback((e: React.ChangeEvent<HTMLInputElement>) => { setDirectSearch(e.target.value); setDirectPage(1); }, [])}
                  className="pl-9"
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-0">
              {directLoading || (directSegment === "birthdays" && birthdaysLoading) ? (
                <div className="flex items-center justify-center py-16 text-muted-foreground">
                  <Clock className="h-5 w-5 animate-spin mr-2" />Carregando...
                </div>
              ) : visibleDirectClients.length === 0 ? (
                <div className="text-center py-16 text-muted-foreground">
                  <Users className="h-12 w-12 mx-auto mb-3 opacity-20" />
                  <p className="font-medium">Base de clientes vazia</p>
                  <p className="text-sm mt-1">Adicione clientes manualmente ou importe via CSV.</p>
                  <div className="flex justify-center gap-2 mt-4">
                    <Button variant="outline" size="sm" onClick={() => setImportOpen(true)}>
                      <Upload className="h-4 w-4 mr-2" />Importar CSV
                    </Button>
                    <Button size="sm" onClick={() => { setEditingClient(null); setFormOpen(true); }}>
                      <Plus className="h-4 w-4 mr-2" />Adicionar
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="divide-y">
                  {visibleDirectClients.map((client: any) => (
                    <div
                      key={client.id}
                      className="flex items-center justify-between px-3 md:px-6 py-3 md:py-4 hover:bg-muted/30 transition-colors cursor-pointer gap-2 group"
                      onClick={() => { setEditingClient(client); setFormOpen(true); }}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="h-9 w-9 rounded-full bg-blue-500/10 flex items-center justify-center flex-shrink-0 group-hover:bg-primary/10 transition-colors">
                          <span className="text-sm font-semibold text-blue-600 group-hover:text-primary">{client.name.charAt(0).toUpperCase()}</span>
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-sm truncate">{client.name}</p>
                          <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-0.5">
                            {client.phone && (
                              <span className="text-xs text-muted-foreground flex items-center gap-1">
                                <Phone className="h-3 w-3 shrink-0" />{client.phone}
                              </span>
                            )}
                            {client.email && (
                              <span className="text-xs text-muted-foreground hidden sm:flex items-center gap-1">
                                <Mail className="h-3 w-3 shrink-0" /><span className="truncate max-w-[140px]">{client.email}</span>
                              </span>
                            )}
                            {client.birthdate && (
                              <span className="text-xs text-muted-foreground hidden md:flex items-center gap-1">
                                <Calendar className="h-3 w-3 shrink-0" />{formatDate(client.birthdate)}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0" onClick={(e) => e.stopPropagation()}>
                        {client.source === "import" && (
                          <Badge variant="outline" className="text-xs hidden sm:flex">Importado</Badge>
                        )}
                        <Button variant="ghost" size="sm" className="opacity-0 group-hover:opacity-100 transition-opacity" onClick={(e) => { e.stopPropagation(); setEditingClient(client); setFormOpen(true); }}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive" onClick={(e) => { e.stopPropagation(); setDeleteClientId(client.id); }}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {directSegment === "all" && directTotalPages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">Página {directPage} de {directTotalPages}</p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setDirectPage((p) => Math.max(1, p - 1))} disabled={directPage === 1}>
                  <ChevronLeft className="h-4 w-4" />Anterior
                </Button>
                <Button variant="outline" size="sm" onClick={() => setDirectPage((p) => Math.min(directTotalPages, p + 1))} disabled={directPage === directTotalPages}>
                  Próxima<ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </TabsContent>
        {/* ── Aba: Reativação de Clientes Inativos ── */}
        <TabsContent value="reactivation" className="space-y-4 mt-4">
          <ReactivationTab />
        </TabsContent>
        {/* ── Aba: Campanha via Agente Nativo ── */}
        <TabsContent value="agentCampaign" className="space-y-4 mt-4">
          <AgentCampaignTab />
        </TabsContent>
      </Tabs>
      {/* Modals */}
      <ClientHistoryModal client={selectedClient} salonId={mySalon?.id} open={historyOpen} onClose={() => setHistoryOpen(false)} />
      <ImportModal open={importOpen} onClose={() => setImportOpen(false)} onSuccess={() => (utils as any).clients.listDirect.invalidate()} />
      <ClientFormModal
        client={editingClient}
        open={formOpen}
        onClose={() => { setFormOpen(false); setEditingClient(null); }}
        onSuccess={() => (utils as any).clients.listDirect.invalidate()}
      />
      <AlertDialog open={deleteClientId !== null} onOpenChange={(v) => { if (!v) setDeleteClientId(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remover cliente?</AlertDialogTitle>
            <AlertDialogDescription>Esta ação não pode ser desfeita.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => { if (deleteClientId) { deleteMutation.mutate({ id: deleteClientId }); setDeleteClientId(null); } }}
            >
              Remover
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
